import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useCart } from '../../context/CartContext';

const Card = styled(Link)`
  background: ${({ theme }) => theme.colors.background.card};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  overflow: hidden;
  transition: all ${({ theme }) => theme.transitions.medium};
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 2px solid transparent;
  position: relative;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${({ theme }) => theme.gradients.gold};
    transform: scaleX(0);
    transition: transform ${({ theme }) => theme.transitions.medium};
    z-index: 1;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${({ theme }) => theme.shadows.gold};
    border-color: ${({ theme }) => theme.colors.metallic.gold};
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  height: 250px;
  overflow: hidden;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 180px;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform ${({ theme }) => theme.transitions.slow};
    filter: brightness(0.98);
  }
  
  ${Card}:hover & img {
    transform: scale(1.15);
    filter: brightness(1);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.05) 100%);
    pointer-events: none;
  }
`;

const Badge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  background: ${props => props.$outOfStock ? '#666' : ({ theme }) => theme.gradients.gold};
  color: ${props => props.$outOfStock ? 'white' : ({ theme }) => theme.colors.primary};
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  z-index: 2;
  box-shadow: ${({ theme }) => theme.shadows.md};
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const CardContent = styled.div`
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0.75rem;
  }
`;

const Category = styled.span`
  font-size: 0.75rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
`;

const Title = styled.h3`
  font-size: 1.1rem;
  color: #333;
  margin: 0 0 0.75rem 0;
  line-height: 1.3;
`;

const Price = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  background: ${({ theme }) => theme.gradients.gold};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
  letter-spacing: 0.5px;
`;

const StockInfo = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.5rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: auto;
  position: relative;
  z-index: 2;
`;

const Button = styled.button`
  background: ${props => props.$primary ? ({ theme }) => theme.gradients.gold : 'transparent'};
  color: ${props => props.$primary ? ({ theme }) => theme.colors.primary : ({ theme }) => theme.colors.metallic.gold};
  border: ${props => props.$primary ? 'none' : ({ theme }) => `2px solid ${theme.colors.metallic.gold}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 0.7rem 1.2rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  flex: ${props => props.$fullWidth ? '1' : 'initial'};
  transition: all ${({ theme }) => theme.transitions.fast};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.gold};
    background: ${props => props.$primary ? ({ theme }) => theme.colors.metallic.goldLight : ({ theme }) => theme.colors.metallic.gold};
    color: ${({ theme }) => theme.colors.primary};
  }
  
  &:disabled {
    background: #ccc;
    border-color: #ccc;
    color: #666;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const QuantityContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
`;

const QuantityLabel = styled.span`
  font-size: 0.9rem;
  margin-right: 0.5rem;
`;

const QuantityInput = styled.input`
  width: 50px;
  height: 30px;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
  font-size: 0.9rem;
  
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const QuantityButton = styled.button`
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  color: #333;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  
  &:first-of-type {
    border-radius: 4px 0 0 4px;
  }
  
  &:last-of-type {
    border-radius: 0 4px 4px 0;
  }
  
  &:hover {
    background-color: #e5e5e5;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ProductCard = ({ product }) => {
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();
  
  // Helper function to strip HTML tags from description
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };
  
  // Get the available stock quantity, default to 0 if not defined
  // Check both 'stock' and 'quantity' for backward compatibility
  const stockQuantity = product.stock || product.quantity || 0;
  const isOutOfStock = stockQuantity <= 0;
  
  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1 && value <= stockQuantity) {
      setQuantity(value);
    }
  };
  
  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };
  
  const incrementQuantity = () => {
    if (quantity < stockQuantity) {
      setQuantity(quantity + 1);
    }
  };
  
  const handleAddToCart = () => {
    if (isOutOfStock) return;
    
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      imageUrl: product.image,
      quantity: quantity,
      stock: stockQuantity
    });
    
    // Reset quantity after adding to cart
    setQuantity(1);
  };
  
  const handleCardClick = (e) => {
    // Prevent navigation if clicking on interactive elements
    if (e.target.closest('button') || e.target.closest('input')) {
      e.preventDefault();
    }
  };
  
  return (
    <Card to={`/product/${product.id}`} onClick={handleCardClick}>
      <ImageContainer>
        <img src={product.image || 'https://via.placeholder.com/300x200?text=Product'} alt={product.name} />
        {product.featured && <Badge>Featured</Badge>}
        {isOutOfStock && <Badge $outOfStock>Out of Stock</Badge>}
      </ImageContainer>
      
      <CardContent>
        <Category>{product.category}</Category>
        <Title>{product.name}</Title>
        <Price>${product.price.toFixed(2)}</Price>
        
        {!isOutOfStock && (
          <>
            <StockInfo>In Stock: {stockQuantity}</StockInfo>
            <QuantityContainer>
              <QuantityLabel>Qty:</QuantityLabel>
              <QuantityButton onClick={(e) => { e.preventDefault(); decrementQuantity(); }} disabled={quantity <= 1}>-</QuantityButton>
              <QuantityInput
                type="number"
                min="1"
                max={stockQuantity}
                value={quantity}
                onChange={(e) => { e.preventDefault(); handleQuantityChange(e); }}
                onClick={(e) => e.preventDefault()}
              />
              <QuantityButton onClick={(e) => { e.preventDefault(); incrementQuantity(); }} disabled={quantity >= stockQuantity}>+</QuantityButton>
            </QuantityContainer>
          </>
        )}
        
        <ButtonContainer>
          <Button 
            $primary 
            $fullWidth 
            onClick={(e) => { e.preventDefault(); handleAddToCart(); }}
            disabled={isOutOfStock}
          >
            {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
          </Button>
        </ButtonContainer>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
